import { Stepper } from '@vnetwork/ui/primitives';
import {
  TICKET_STATUS_ESCALATED,
  TICKET_STATUS_WORK_IN_PROGRESS,
} from '@/api/tickets-constants';

type StepKey = 'opening' | 'work-in-progress' | 'completed' | 'closed';

interface Step {
  key: StepKey;
  label: string;
  hint: string;
}

const ALL_STEPS: Step[] = [
  {
    key: 'opening',
    label: 'New',
    hint: 'Ticket created, awaiting pickup.',
  },
  {
    key: 'work-in-progress',
    label: 'In Progress',
    hint: 'Ticket is being worked on by the technical team.',
  },
  {
    key: 'completed',
    label: 'Resolved',
    hint: 'Ticket resolved. Service tickets end here; regular tickets await manager close.',
  },
  {
    key: 'closed',
    label: 'Closed',
    hint: 'Ticket fully closed (regular tickets only — not applicable to service tickets).',
  },
];

interface TicketWorkflowStepperProps {
  currentStatus: string;
  /** Service tickets (ticket_type_key service_*) end at "completed", skip "closed". */
  isServiceTicket?: boolean;
}

/**
 * Horizontal stepper: opening → work-in-progress → completed → closed.
 * Service tickets skip "closed".
 * "escalated" maps to the work-in-progress stage (not a standalone step).
 */
export function TicketWorkflowStepper({
  currentStatus,
  isServiceTicket = false,
}: TicketWorkflowStepperProps) {
  const steps = isServiceTicket
    ? ALL_STEPS.filter((s) => s.key !== 'closed')
    : ALL_STEPS;

  // escalated is a flag on a work-in-progress ticket, not its own stage
  const stageKey =
    currentStatus === TICKET_STATUS_ESCALATED
      ? TICKET_STATUS_WORK_IN_PROGRESS
      : currentStatus;

  const currentIndex = steps.findIndex((s) => s.key === stageKey);
  // Guard: unknown status → 0 (not -1) so the ticket doesn't look brand new
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;

  return (
    <Stepper
      aria-label="Ticket workflow progress"
      steps={steps.map((s) => ({ label: s.label, description: s.hint }))}
      current={safeIndex}
    />
  );
}
